import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

export default function ProductPQMB() {
  const { t } = useLanguage();

  const pqmbPoints = [
    {
      icon: 'ri-flask-line',
      title: t('高濃度預分散', 'High-Concentration Pre-Dispersion', '高濃度事前分散'),
      desc: t(
        'PQernel 以高濃度形式預先分散於塑膠粒中，確保添加均勻、效果穩定。',
        'PQernel is pre-dispersed in plastic pellets at high concentration, ensuring uniform addition and stable performance.',
        'PQernelが高濃度でプラスチックペレットに事前分散され、均一な添加と安定した効果を確保します。'
      ),
    },
    {
      icon: 'ri-settings-3-line',
      title: t('精準計量添加', 'Precise Metered Addition', '精密計量添加'),
      desc: t(
        '方便產線依配方設計精準添加，提升操作便利性、穩定度與批次重複性。',
        'Convenient for production lines to add precisely according to formulation design, improving operational convenience, stability, and batch repeatability.',
        '配合設計に従って生産ラインで精密に添加でき、操作の利便性・安定性・バッチ再現性を向上させます。'
      ),
    },
    {
      icon: 'ri-leaf-line',
      title: t('適合品牌端導入', 'Suitable for Brand Implementation', 'ブランド導入に適合'),
      desc: t(
        '適合導入品牌端低碳產品線與 ESG 專案，支援再生料比例提升與碳足跡計算。',
        'Suitable for implementing brand low-carbon product lines and ESG projects, supporting increased recycled material ratios and carbon footprint calculations.',
        'ブランドの低炭素製品ラインとESGプロジェクトへの導入に適しており、再生材料比率の向上とカーボンフットプリント計算をサポートします。'
      ),
    },
  ];

  return (
    <section className="section-padding bg-[#F5F8FB] overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-1 h-10 rounded-full bg-gradient-to-b from-brand-blue to-brand-green flex-shrink-0" />
          <div>
            <p className="text-[1rem] font-semibold text-brand-blue uppercase tracking-widest">{t('母粒技術', 'Masterbatch', 'マスターバッチ')}</p>
            <h2 className="font-bold text-neutral-dark" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.3' }}>
              PQMB PolyQuantum Masterbatch
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-52 h-52 opacity-35 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(2,115,194,0.18) 0%, transparent 70%)' }} aria-hidden="true" />
            <div className="relative rounded-2xl overflow-hidden w-full" style={{ height: '380px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=polymer%20masterbatch%20concentrate%20pellets%20blue%20green%20colorful%20granules%20in%20industrial%20container%20professional%20product%20photography%20clean%20white%20background%20blue%20accent%20lighting%20macro%20detail%20plastic%20additive%20material%20science&width=640&height=380&seq=pqmb-detail-v1&orientation=landscape"
                alt={t('PQMB PolyQuantum Masterbatch', 'PQMB PolyQuantum Masterbatch', 'PQMB PolyQuantum マスターバッチ')}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-white/50 rounded-xl px-4 py-3 border border-neutral-200/60" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.18)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-brand-blue-light flex-shrink-0">
                      <i className="ri-capsule-line text-brand-blue text-lg"></i>
                    </div>
                    <div>
                      <p className="font-bold text-neutral-dark text-[1rem]">
                        {t('PQMB 高濃度母粒', 'PQMB High-Concentration Masterbatch', 'PQMB 高濃度マスターバッチ')}
                      </p>
                      <p className="text-neutral-mid text-[0.875rem]">
                        {t('PQernel® 延伸應用 · 精準計量', 'PQernel® Extended Application · Precise Metering', 'PQernel® 延伸応用・精密計量')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="text-neutral-mid mb-8" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>
              {t(
                'PQMB 是高濃度 PQernel 母粒，將 PQernel 以高濃度形式預先分散於塑膠粒中，方便產線依配方設計精準添加，提升操作便利性、穩定度與重複性。',
                'PQMB is a high-concentration PQernel masterbatch that pre-disperses PQernel at high concentration in plastic pellets, enabling production lines to add precisely according to formulation design, improving operational convenience, stability, and repeatability.',
                'PQMBはPQernelを高濃度でプラスチックペレットに事前分散した高濃度マスターバッチです。生産ラインが配合設計に従って精密に添加でき、操作の利便性・安定性・再現性を向上させます。'
              )}
            </p>

            <div className="space-y-4 mb-8">
              {pqmbPoints.map((pt) => (
                <div key={pt.title} className="flex items-start gap-4 p-5 rounded-xl bg-white border border-neutral-border" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-blue-light flex-shrink-0">
                    <i className={`${pt.icon} text-brand-blue text-xl`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-dark text-[1.0625rem] mb-1">{pt.title}</p>
                    <p className="text-neutral-mid text-[1rem] leading-relaxed">{pt.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 rounded-xl bg-brand-blue-light border border-brand-blue/20 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-brand-blue text-lg"></i>
                </div>
                <p className="text-neutral-dark text-[1rem] leading-relaxed">
                  <strong>
                    {t('PQMB vs PQernel® 直接添加：', 'PQMB vs. Direct PQernel® Addition: ', 'PQMB vs PQernel® 直接添加：')}
                  </strong>
                  {t(
                    'PQMB 適合需要精準計量與批次穩定的產線；PQernel® 直接添加則適合靈活配方調整的場景。',
                    'PQMB is suitable for production lines requiring precise metering and batch stability; direct PQernel® addition is suitable for scenarios requiring flexible formulation adjustments.',
                    'PQMBは精密計量とバッチ安定性が必要な生産ラインに適しており、PQernel®の直接添加は柔軟な配合調整が必要なシーンに適しています。'
                  )}
                </p>
              </div>
            </div>

            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-blue text-white text-[1.0625rem] font-semibold hover:bg-brand-blue-dark transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-send-line"></i>
              {t('詢問 PQMB 產品規格', 'Inquire About PQMB Specifications', 'PQMB製品仕様についてお問い合わせ')}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
